import React from 'react'
import PropTypes from 'prop-types'
import { Field } from 'redux-form'
import { formatYearMonthDate } from 'utils'
import { Input } from 'lp-components'
import {
  NestedAttributesFieldArray,
  ReadOnlyCellInput,
  ReadOnlyCellInputWithTooltip,
  DateInput,
  OptionalAutoSuggestInput,
  CloudinaryCustomizedFileInput,
} from 'components'
import { UploadIcon } from 'components/icons'
import { isString } from 'lodash'
import { CLOUD_RESIZE_PRESET } from 'config'

const propTypes = {
  credentials: PropTypes.array,
  change: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  artistId: PropTypes.number.isRequired,
}

const LICENSE_OPTIONS = [
  'Arts Integration Certification',
  'CPR / AED Certification',
  'Culturally Responsive Certification',
  'Mandated Reporter Certification',
  'Social-Emotional Learning (SEL) Certification',
  'Substitute Teaching License',
  'TESOL / ESL Certification',
  'Title IX Certification',
  'Trauma-Informed / Healing-Centered Certification',
  'Youth Development Certification',
]
const defaultProps = {}

function parseOption(option) {
  if (!option) return ''
  if (isString(option)) return option
  return option.id
}

function LicenseRow({
  fields,
  index,
  change,
  credentials,
  flashErrorMessage,
  artistId,
}) {
  const field = fields.name ? `${fields.name}[${index}]` : `${index}`
  const credential = fields.get(index)
  const isEditing = credential?.isEditing

  if (credential.credentialType !== 'license') {
    return null
  }

  const updateField = (field, value) => {
    const updatedCredential = { ...credential, [field]: value }
    const updatedCredentials = [...credentials]
    updatedCredentials[index] = updatedCredential
    change('credentials', updatedCredentials)
  }

  const setIsEditing = (isEditing) => {
    const canSave =
      credential.institutionName &&
      credential.credentialName &&
      credential.issueDate
    if (!isEditing) {
      if (!canSave) {
        flashErrorMessage('Please fill in all fields before saving.')
        return
      }
    }
    updateField('isEditing', isEditing)
  }

  const CustomFileInput = (
    <div className="license-file-input font-bold">
      <UploadIcon />
      <u>Upload</u>
    </div>
  )

  return (
    <div key={credential.id || index} className="form-table-row">
      <div className="form-table-cell">
        <Field
          name={field + '.credentialName'}
          label={false}
          component={isEditing ? OptionalAutoSuggestInput : ReadOnlyCellInput}
          inputProps={{
            placeholder: 'Enter title',
          }}
          parse={parseOption}
          suggestions={LICENSE_OPTIONS}
          shouldRenderSuggestions={() => true}
          className="col-4"
        />
      </div>
      <div className="form-table-cell">
        <Field
          name={field + '.institutionName'}
          label={false}
          component={isEditing ? Input : ReadOnlyCellInput}
          placeholder="Enter school name"
        />
      </div>
      <div className="form-table-cell">
        <Field
          name={field + '.issueDate'}
          label={false}
          component={isEditing ? DateInput : ReadOnlyCellInput}
          placeholderText="MM/DD/YYYY"
          popperPlacement="top"
          className="label-block"
          format={formatYearMonthDate}
          parse={formatYearMonthDate}
        />
      </div>
      <div className="form-table-cell">
        <div className="license-file-input">
          <Field
            name={field + '.linkCredential'}
            label={false}
            component={isEditing ? Input : ReadOnlyCellInputWithTooltip}
            placeholder="Enter link to credential"
            showTooltip={!isEditing}
            tooltipContent={credential.linkCredential}
          />
          {isEditing && (
            <>
              <span>or </span>
              <Field
                name={field + '.linkCredential'}
                label={false}
                component={CloudinaryCustomizedFileInput}
                customComponent={CustomFileInput}
                uploadPath={`/artists/users/credentials/${artistId}`}
                uploadPreset={CLOUD_RESIZE_PRESET}
                onUploadSuccess={({ secureUrl }) => {
                  updateField('linkCredential', secureUrl)
                }}
                previewComponent={() => null}
                className="col-4"
              />
            </>
          )}
        </div>
      </div>
      <div className="form-table-cell">
        <button
          type="button"
          className="button-primary"
          onClick={() => setIsEditing(!isEditing)}
        >
          {isEditing ? 'Save' : 'Edit'}
        </button>
      </div>
      <div className="form-table-cell">
        <button
          type="button"
          className="remove-row"
          aria-label={`Remove ${credential.institutionName || 'license'} entry`}
          onClick={() => fields.destroy(index)}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </div>
  )
}

function LicenseFields({
  fields,
  change,
  credentials,
  flashErrorMessage,
  artistId,
}) {
  return (
    <>
      {fields.map((_, i) => {
        return (
          <LicenseRow
            key={`credential-${i}`}
            index={i}
            fields={fields}
            change={change}
            credentials={credentials}
            flashErrorMessage={flashErrorMessage}
            artistId={artistId}
          />
        )
      })}
    </>
  )
}

function LicenseSection({ change, credentials, flashErrorMessage, artistId }) {
  const addLicenseEntry = () => {
    const newCredential = {
      institutionName: '',
      credentialType: 'license',
      isEditing: true,
    }
    change('credentials', [...credentials, newCredential])
  }
  return (
    <section>
      <div>
        <div className="form-table">
          <div className="form-table-heading">
            <div className="form-table-row">
              <div className="form-table-head">Title</div>
              <div className="form-table-head">Issuing Organization</div>
              <div className="form-table-head">Issue Date</div>
              <div className="form-table-head">Link to credential</div>
              <div className="form-table-head" style={{ width: 70 }}></div>
              <div className="form-table-head" style={{ width: 70 }}></div>
              <div className="form-table-head" style={{ width: 20 }}></div>
            </div>
          </div>
          <div className="form-table-body">
            <NestedAttributesFieldArray
              name="credentials"
              component={LicenseFields}
              change={change}
              credentials={credentials}
              flashErrorMessage={flashErrorMessage}
              artistId={artistId}
            />
          </div>
        </div>
      </div>
      <button type="button" className="link-black" onClick={addLicenseEntry}>
        + Add New License
      </button>
    </section>
  )
}

LicenseSection.propTypes = propTypes
LicenseSection.defaultProps = defaultProps

export default LicenseSection
